/**
 * A CARD'S EFFECTIVE PROGRESS — the one number every surface should read.
 *
 * A card's percent-complete can come from these places, in strict precedence:
 *
 *   1. MANUAL — a person (the doer or a manager) typed a %. An explicit statement,
 *      it always wins.
 *   2. COMBINED — nobody typed a %, and the card has BOTH its own checklist
 *      (issue.checklist) AND the board has a Definition of Done (doneChecks) with
 *      items. The two are POOLED: every ticked step of both lists ÷ every step of
 *      both lists. A DoD and a card checklist are both real work, so each moves the
 *      bar — weighted by how many steps it has (Vinnie, 3 Nov). 2 of 4 checklist +
 *      1 of 2 DoD = 3 of 6 = 50%.
 *   3. CARD-CHECKLIST — only the card's own checklist is set: its done ÷ total.
 *   4. DONE-CHECKS — only the board DoD is set: its ticked ÷ total.
 *
 * When none is present the card has no progress to show (null) — it is not "0%",
 * it is "unmeasured", and the health read keeps treating it as such. An EMPTY card
 * checklist (length 0) is "no checklist".
 *
 * Each derivation's denominator is the WHOLE list — the card checklist's own
 * length, or the board's full DoD list length — never the count of keys that
 * happen to sit on the card. Pure: no store, no React — unit-tested in
 * scripts/test-progress-derive.ts.
 */
import type { Issue } from './types';

/** Where a shown % came from — so a surface can say where it is derived from.
 *  'card-checklist' = the card's own checklist; 'done-checks' = the board DoD. */
export type ProgressSource = 'manual' | 'card-checklist' | 'done-checks' | 'combined' | null;

export interface EffectiveProgress {
  /** 0..100, or null when the card has neither a typed % nor any DoD to derive from. */
  pct: number | null;
  source: ProgressSource;
  /** The pooled done / total behind a DERIVED %, so a surface can name the count
   *  ("N of M") without recomputing. Null for a typed % or when unmeasured. */
  done: number | null;
  total: number | null;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * The card's progress and where it came from. `doneChecks` is the board's DoD
 * list (ProjectMeta.doneChecks) — pass it wherever the board meta is in scope so
 * a card with no typed % and no checklist still contributes its ticked-off share;
 * omit it (or pass an empty list) where the board has no definition of done and
 * only a typed % or the card's own checklist counts.
 */
export function effectiveProgress(
  issue: Pick<Issue, 'progressPct' | 'checks' | 'checklist'>,
  doneChecks?: readonly string[] | null,
): EffectiveProgress {
  const raw = issue.progressPct;
  if (raw != null && !Number.isNaN(raw)) {
    return { pct: clamp(Math.round(raw), 0, 100), source: 'manual', done: null, total: null };
  }
  // No typed %: derive from the card's own checklist AND the board DoD. When BOTH are
  // present the two are POOLED — every ticked step of both ÷ every step of both — so a
  // DoD and a card checklist each move the bar, weighted by how many steps each has
  // (Vinnie, 3 Nov). With only one set, that one drives it. An empty card checklist
  // (length 0) is "no checklist".
  const list = issue.checklist;
  const hasCl = !!(list && list.length > 0);
  const required = doneChecks ?? [];
  const hasDod = required.length > 0;
  const clDone = hasCl ? list!.reduce((n, it) => n + (it.done ? 1 : 0), 0) : 0;
  const clTotal = hasCl ? list!.length : 0;
  const dodDone = hasDod ? required.reduce((n, c) => n + (issue.checks?.[c] === true ? 1 : 0), 0) : 0;
  const dodTotal = hasDod ? required.length : 0;
  if (hasCl && hasDod) {
    const done = clDone + dodDone;
    const total = clTotal + dodTotal;
    return { pct: Math.round((done / total) * 100), source: 'combined', done, total };
  }
  if (hasCl) {
    return { pct: Math.round((clDone / clTotal) * 100), source: 'card-checklist', done: clDone, total: clTotal };
  }
  if (hasDod) {
    return { pct: Math.round((dodDone / dodTotal) * 100), source: 'done-checks', done: dodDone, total: dodTotal };
  }
  return { pct: null, source: null, done: null, total: null };
}

/** Just the number (or null) — for callers that don't need the provenance. */
export function effectiveProgressPct(
  issue: Pick<Issue, 'progressPct' | 'checks' | 'checklist'>,
  doneChecks?: readonly string[] | null,
): number | null {
  return effectiveProgress(issue, doneChecks).pct;
}
